import { lazy, Suspense, type ReactNode } from 'react';
import type { PropertyFileResponse, PropertyFileTreeNode } from '@/shared/api';
import { collectFilesDeep, matchesQuery } from './akteBrowserNav';
import { AkteContentFileRow } from './AkteContentFileRow';
import { AkteEmptyFolder } from './AkteEmptyFolder';
import { AkteFolderRows } from './AkteFolderRows';
import { AkteSelectionBar } from './AkteSelectionBar';
import { bulkAvailability } from './akteBulkRules';
import { folderDisplayName } from './akteFolderNames';
import { isCustomFolder } from './akteFolderRules';
import type { useAkteDragMove } from './useAkteDragMove';
import type { AkteSelectionApi } from './useAkteSelection';
import type { AkteBulkHandlers } from './useAkteBulkDialogs';

// Kachel-Ansicht zieht react-pdf/pdf.js nach — lazy, damit die Listen-Ansicht (Default)
// den PDF-Renderer nicht ins Initial-Bundle zwingt.
const AkteContentTile = lazy(() =>
  import('./AkteContentTile').then((m) => ({ default: m.AkteContentTile })),
);
import type { AkteViewMode } from './useAkteBrowser';
import type { FileActions } from './akteFileActions';

interface Props {
  node: PropertyFileTreeNode;
  propertyId: string;
  viewMode: AkteViewMode;
  search: string;
  selection: AkteSelectionApi;
  actions: FileActions;
  onNavigate: (path: string) => void;
  onUploadHere: () => void;
  /** Kontextmenü pro Unterordner (Custom) — von C2 injiziert. */
  folderMenu?: (node: PropertyFileTreeNode) => ReactNode;
  /** Geteilte Drag-Instanz (Quelle Content-Zeilen, Ziele auch Sidebar). */
  drag: ReturnType<typeof useAkteDragMove>;
  /** Sammelaktions-Auslöser (öffnen Dialog / starten Op mit der aktuellen Auswahl). */
  bulk: AkteBulkHandlers;
}

/** Content-Zone: Sammel-Toolbar, Unterordner-Zeilen, dann Datei-Zeilen (List/Kacheln). */
export function AkteContentList({
  node,
  propertyId,
  viewMode,
  search,
  selection,
  actions,
  onNavigate,
  onUploadHere,
  folderMenu,
  drag,
  bulk,
}: Props) {
  const { dragProps, dropProps } = drag;
  const searching = search.trim().length > 0;
  const files: PropertyFileResponse[] = searching
    ? collectFilesDeep(node).filter((f) => matchesQuery(f, search))
    : node.files;
  const folders = searching ? [] : node.folders;

  if (folders.length === 0 && files.length === 0) {
    return (
      <AkteEmptyFolder
        folderName={folderDisplayName(node.path)}
        custom={isCustomFolder(node.path) || node.path === ''}
        searching={searching}
        onUpload={onUploadHere}
      />
    );
  }

  const orderedIds = files.map((f) => f.id);
  const selectionActive = selection.count > 0;
  // Auswahl ist ordner-lokal (räumt bei navigateTo/Suche auf) → die sichtbaren Dateien sind
  // eine Obermenge der Selektion; `files.filter(isSelected)` ist damit die vollständige Auswahl.
  const selectedFiles = files.filter((f) => selection.isSelected(f.id));
  const rowSelection = (file: PropertyFileResponse) => ({
    selected: selection.isSelected(file.id),
    selectionActive,
    onClick: (mods: Parameters<AkteSelectionApi['handleClick']>[2]) =>
      selection.handleClick(file.id, orderedIds, mods),
    onToggleSelect: () => selection.toggle(file.id),
  });

  const bar = selectionActive && (
    <AkteSelectionBar
      count={selection.count}
      totalInView={orderedIds.length}
      onToggleAll={() =>
        selection.count === orderedIds.length ? selection.clear() : selection.replaceAll(orderedIds)
      }
      onClear={selection.clear}
      bulk={{
        ...bulkAvailability(selectedFiles),
        onMove: () => bulk.onMove(selectedFiles),
        onReclassify: () => bulk.onReclassify(selectedFiles),
        onDelete: () => bulk.onDelete(selectedFiles),
        onDownload: () => bulk.onDownload(selectedFiles),
      }}
    />
  );

  const folderRows = <AkteFolderRows {...{ folders, onNavigate, folderMenu, dropProps }} />;

  if (viewMode === 'tiles' && !searching) {
    return (
      <div className="space-y-4">
        {bar}
        {folders.length > 0 && <ul className="space-y-1">{folderRows}</ul>}
        <Suspense fallback={<p className="text-ink-faint text-sm">Lade Vorschauen…</p>}>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] sm:gap-4">
            {files.map((file) => (
              <AkteContentTile
                key={file.id}
                propertyId={propertyId}
                file={file}
                actions={actions}
                dragProps={dragProps(file)}
                {...rowSelection(file)}
              />
            ))}
          </div>
        </Suspense>
      </div>
    );
  }

  return (
    <>
      {bar}
      <ul className="space-y-1">
        {folderRows}
        {files.map((file) => (
          <AkteContentFileRow
            key={file.id}
            file={file}
            actions={actions}
            dragProps={dragProps(file)}
            {...rowSelection(file)}
          />
        ))}
      </ul>
    </>
  );
}
